<template>
  <div :class="containerClass" :style="containerStyle">
    <div
      :class="wrapperClass"
      :style="wrapperStyle"
      class="pagination-wrapper d-flex flex-row"
    >
      <!-- Page Info -->
      <slot
        name="pageInfo"
        :startItem="startItem"
        :endItem="endItem"
        :totalItems="totalItems"
      >
        <div
          v-if="showPageInfo"
          :class="pageInfoClassCombined"
          :style="pageInfoStyle"
        >
          <span class="page-info__label">{{ $t("showing") }}</span>
          <span class="page-info__badge"> {{ startItem }}-{{ endItem }} </span>
          <span class="page-info__total"
            >{{ $t("of") }} {{ totalItems }} {{ $t("items") }}</span
          >
        </div>
      </slot>

      <!-- Previous Button -->
      <slot
        name="previous"
        :onClick="goToPreviousPage"
        :disabled="isPreviousDisabled"
      >
        <button
          v-if="showPrevious"
          @click="goToPreviousPage"
          :disabled="isPreviousDisabled"
          :class="[
            previousButtonClass,
            'pagination-prev',
            { disabled: isPreviousDisabled },
          ]"
          :style="previousButtonStyle"
        >
          <slot name="previousIcon">
            <v-icon size="20" class="previous-icon">{{
              $vuetify.rtl ? "mdi-chevron-right" : "mdi-chevron-left"
            }}</v-icon>
          </slot>
          <span v-if="previousText">{{
            previousText === "Previous" ? $t("previous") : $t(previousText)
          }}</span>
        </button>
      </slot>

      <div class="page-numbers-group">
        <!-- First Page -->
        <slot
          name="firstPage"
          :onClick="() => goToPage(1)"
          :active="pageNumber === 1"
          v-if="showFirstPage"
        >
          <button
            @click="goToPage(1)"
            :class="[pageButtonClass, { active: pageNumber === 1 }]"
            :style="getPageButtonStyle(1)"
          >
            1
          </button>
        </slot>

        <!-- First Dots -->
        <slot name="firstDots" v-if="showFirstDots">
          <span :class="dotsClass" :style="dotsStyle">{{ dotsText }}</span>
        </slot>

        <!-- Page Numbers -->
        <template v-for="page in visiblePages">
          <slot
            name="pageButton"
            :page="page"
            :onClick="() => goToPage(page)"
            :active="pageNumber === page"
          >
            <button
              :key="`page-${page}`"
              @click="goToPage(page)"
              :class="[pageButtonClass, { active: pageNumber === page }]"
              :style="getPageButtonStyle(page)"
            >
              {{ page }}
            </button>
          </slot>
        </template>

        <!-- Last Dots -->
        <slot name="lastDots" v-if="showLastDots">
          <span :class="dotsClass" :style="dotsStyle">{{ dotsText }}</span>
        </slot>

        <!-- Last Page -->
        <slot
          name="lastPage"
          :onClick="() => goToPage(pageCount)"
          :active="pageNumber === pageCount"
          v-if="showLastPage && calculatedPageCount > 1"
        >
          <button
            @click="goToPage(pageCount)"
            :class="[pageButtonClass, { active: pageNumber === pageCount }]"
            :style="getPageButtonStyle(pageCount)"
          >
            {{ pageCount }}
          </button>
        </slot>
      </div>

      <!-- Next Button -->
      <slot name="next" :onClick="goToNextPage" :disabled="isNextDisabled">
        <button
          v-if="showNext"
          @click="goToNextPage"
          :disabled="isNextDisabled"
          :class="[
            nextButtonClass,
            'pagination-next',
            { disabled: isNextDisabled },
          ]"
          :style="nextButtonStyle"
        >
          <span v-if="nextText">{{
            nextText === "Next" ? $t("next") : $t(nextText)
          }}</span>
          <slot name="nextIcon">
            <v-icon size="20" class="next-icon">{{
              $vuetify.rtl ? "mdi-chevron-left" : "mdi-chevron-right"
            }}</v-icon>
          </slot>
        </button>
      </slot>

      <div class="mobile-page-input">
        <input
          type="number"
          min="1"
          :max="calculatedPageCount"
          v-model.number="pageInput"
          @keyup.enter="applyPageInput"
          class="mobile-page-input__field"
          aria-label="Go to page"
        />
        <button
          type="button"
          class="mobile-page-input__button"
          @click="applyPageInput"
        >
          {{ $t("go") }}
        </button>
        <span class="mobile-page-input__summary">
          {{ $t("page") }} {{ pageNumber }} / {{ calculatedPageCount || 1 }}
        </span>
      </div>

      <!-- Items Per Page Selector -->
      <slot
        name="itemsPerPage"
        :items="itemsPerPageOptions"
        :value="itemPerPage"
        :onChange="handleItemsPerPageChange"
      >
        <div
          v-if="showItemsPerPage"
          :class="itemsPerPageContainerClasses"
          :style="itemsPerPageContainerStyle"
        >
          <div class="items-per-page__content">
            <span v-if="itemsPerPageLabel" :class="itemsPerPageLabelClasses">
              {{
                itemsPerPageLabel === "Items per page:"
                  ? $t("items_per_page")
                  : $t(itemsPerPageLabel)
              }}
            </span>
            <div class="items-per-page__control">
              <select
                :value="itemPerPage"
                @change="handleItemsPerPageChange"
                :class="itemsPerPageSelectClasses"
                :style="itemsPerPageSelectStyle"
              >
                <option
                  v-for="option in itemsPerPageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
              <v-icon size="16" class="items-per-page__chevron"
                >mdi-chevron-down</v-icon
              >
            </div>
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaginationContainerNew",
  data() {
    const initialPage = this.getpageNumber || this.currentPage || 1;
    return {
      internalCurrentPage: initialPage,
      pageInput: initialPage,
    };
  },
  watch: {
    currentPage: {
      handler(newVal) {
        this.internalCurrentPage = newVal;
      },
      immediate: true,
    },
    getpageNumber: {
      handler(newVal) {
        if (newVal) {
          this.internalCurrentPage = newVal;
        }
      },
      immediate: true,
    },
    pageNumber: {
      handler(newVal) {
        this.pageInput = newVal;
      },
      immediate: true,
    },
  },
  props: {
    // Legacy Props (for backward compatibility)
    setitemPerPage: { type: Function },
    getitemPerPage: { type: Number },
    getpageNumber: { type: Number },
    setpageNumber: { type: Function },
    updateTableContent: { type: Function },

    // Core Pagination Props
    currentPage: { type: Number, default: 1 },
    totalItems: { type: Number, required: true },
    itemsPerPage: { type: Number, default: 10 },
    pageCount: { type: Number, default: null },

    // Visibility Props
    showPrevious: { type: Boolean, default: true },
    showNext: { type: Boolean, default: true },
    showFirstPage: { type: Boolean, default: true },
    showLastPage: { type: Boolean, default: true },
    showPageInfo: { type: Boolean, default: true },
    showItemsPerPage: { type: Boolean, default: true },

    // Text Props
    previousText: { type: String, default: "Previous" },
    nextText: { type: String, default: "Next" },
    dotsText: { type: String, default: "..." },
    itemsPerPageLabel: { type: String, default: "Items per page:" },

    // Icon Props
    previousIcon: { type: String, default: "mdi mdi-chevron-left" },
    nextIcon: { type: String, default: "mdi mdi-chevron-right" },

    // Behavior Props
    visiblePagesCount: { type: Number, default: 5 },
    itemsPerPageOptions: {
      type: Array,
      default: () => [
        { value: 10, text: "10" },
        { value: 25, text: "25" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],
    },

    // Style Props - Container
    containerClass: {
      type: [String, Array, Object],
      default: "d-flex justify-content-center align-items-center mt-1 mb-1",
    },
    containerStyle: { type: Object, default: () => ({}) },

    // Style Props - Wrapper
    wrapperClass: {
      type: [String, Array, Object],
      default: "card px-4 py-2 p-0 pagination-container",
    },
    wrapperStyle: { type: Object, default: () => ({}) },

    // Style Props - Buttons
    pageButtonClass: {
      type: [String, Array, Object],
      default: "pagination-btn",
    },
    pageButtonStyle: { type: Object, default: () => ({}) },
    activePageButtonStyle: { type: Object, default: () => ({}) },

    previousButtonClass: {
      type: [String, Array, Object],
      default: "pagination-btn nav-btn",
    },
    previousButtonStyle: { type: Object, default: () => ({}) },

    nextButtonClass: {
      type: [String, Array, Object],
      default: "pagination-btn nav-btn",
    },
    nextButtonStyle: { type: Object, default: () => ({}) },

    // Style Props - Other Elements
    dotsClass: { type: [String, Array, Object], default: "pagination-dots" },
    dotsStyle: { type: Object, default: () => ({}) },

    pageInfoClass: { type: [String, Array, Object], default: "page-info" },
    pageInfoStyle: { type: Object, default: () => ({}) },

    itemsPerPageContainerClass: {
      type: [String, Array, Object],
      default: "items-per-page-container",
    },
    itemsPerPageContainerStyle: { type: Object, default: () => ({}) },

    itemsPerPageLabelClass: {
      type: [String, Array, Object],
      default: "items-per-page-label",
    },
    itemsPerPageSelectClass: {
      type: [String, Array, Object],
      default: "items-per-page-select",
    },
    itemsPerPageSelectStyle: { type: Object, default: () => ({}) },

    // Color Props
    primaryColor: { type: String, default: "#8950FC" },
    secondaryColor: { type: String, default: "#f0f0f0" },
    textColor: { type: String, default: "#333" },
    disabledColor: { type: String, default: "#ccc" },
  },

  computed: {
    pageNumber() {
      // Support legacy prop or use internal state
      return this.getpageNumber || this.internalCurrentPage;
    },

    itemPerPage() {
      // Support legacy prop or use new prop
      return this.getitemPerPage || this.itemsPerPage;
    },

    calculatedPageCount() {
      return this.pageCount || Math.ceil(this.totalItems / this.itemPerPage);
    },

    isPreviousDisabled() {
      return this.pageNumber <= 1;
    },

    isNextDisabled() {
      return this.pageNumber >= this.calculatedPageCount;
    },

    startItem() {
      return (this.pageNumber - 1) * this.itemPerPage + 1;
    },

    endItem() {
      return Math.min(this.pageNumber * this.itemPerPage, this.totalItems);
    },

    pageInfoText() {
      return `Showing ${this.startItem}-${this.endItem} of ${this.totalItems} results`;
    },

    visiblePages() {
      const pages = [];
      const halfVisible = Math.floor(this.visiblePagesCount / 2);
      let start = Math.max(2, this.pageNumber - halfVisible);
      let end = Math.min(
        this.calculatedPageCount - 1,
        this.pageNumber + halfVisible
      );

      // Adjust if we're near the beginning or end
      if (start === 2 && end < this.calculatedPageCount - 1) {
        end = Math.min(
          this.calculatedPageCount - 1,
          start + this.visiblePagesCount - 1
        );
      }
      if (end === this.calculatedPageCount - 1 && start > 2) {
        start = Math.max(2, end - this.visiblePagesCount + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    showFirstDots() {
      return (
        this.calculatedPageCount > this.visiblePagesCount &&
        this.pageNumber > Math.floor(this.visiblePagesCount / 2) + 1
      );
    },

    showLastDots() {
      return (
        this.calculatedPageCount > this.visiblePagesCount &&
        this.pageNumber <
          this.calculatedPageCount - Math.floor(this.visiblePagesCount / 2)
      );
    },

    pageInfoClassCombined() {
      return this.combineClasses("page-info", this.pageInfoClass);
    },

    itemsPerPageContainerClasses() {
      return this.combineClasses(
        "items-per-page",
        this.itemsPerPageContainerClass
      );
    },

    itemsPerPageLabelClasses() {
      return this.combineClasses(
        "items-per-page__label",
        this.itemsPerPageLabelClass
      );
    },

    itemsPerPageSelectClasses() {
      return this.combineClasses(
        "items-per-page__select",
        this.itemsPerPageSelectClass
      );
    },
  },

  methods: {
    applyPageInput() {
      if (!this.calculatedPageCount) {
        return;
      }

      let target = parseInt(this.pageInput, 10);

      if (Number.isNaN(target)) {
        this.pageInput = this.pageNumber;
        return;
      }

      target = Math.min(Math.max(target, 1), this.calculatedPageCount);
      this.pageInput = target;
      this.goToPage(target);
    },

    combineClasses(baseClass, provided) {
      if (!provided) {
        return baseClass;
      }

      if (typeof provided === "string") {
        const tokens = provided.split(" ").filter(Boolean);
        if (tokens.includes(baseClass)) {
          return provided;
        }
        return [baseClass, provided];
      }

      if (Array.isArray(provided)) {
        return [baseClass, ...provided];
      }

      // Assume object for class binding
      return [baseClass, provided];
    },

    goToPage(page) {
      if (
        page >= 1 &&
        page <= this.calculatedPageCount &&
        page !== this.pageNumber
      ) {
        this.internalCurrentPage = page;

        // Support legacy function or emit event
        if (this.setpageNumber) {
          this.setpageNumber(page);
          if (this.updateTableContent) {
            this.updateTableContent();
          }
        } else {
          this.$emit("page-changed", page);
        }
      }
    },

    goToPreviousPage() {
      if (!this.isPreviousDisabled) {
        this.goToPage(this.pageNumber - 1);
      }
    },

    goToNextPage() {
      if (!this.isNextDisabled) {
        this.goToPage(this.pageNumber + 1);
      }
    },

    handleItemsPerPageChange(event) {
      const value = parseInt(event.target.value);

      // Support legacy function or emit event
      if (this.setitemPerPage) {
        this.setitemPerPage(value);
        if (this.updateTableContent) {
          this.updateTableContent();
        }
      } else {
        this.$emit("items-per-page-changed", value);
      }

      // Reset to first page when items per page changes
      this.goToPage(1);
    },

    getPageButtonStyle(page) {
      const baseStyle = { ...this.pageButtonStyle };
      if (page === this.pageNumber) {
        return {
          ...baseStyle,
          ...this.activePageButtonStyle,
          backgroundColor: this.primaryColor,
          color: "white",
        };
      }
      return baseStyle;
    },
  },

  // Legacy props support for backward compatibility
  created() {
    // Support old prop names
    if (this.$props.getpageNumber !== undefined) {
      this.pageNumber = this.$props.getpageNumber;
    }
    if (this.$props.getitemPerPage !== undefined) {
      this.itemPerPage = this.$props.getitemPerPage;
    }
  },
};
</script>

<style scoped lang="scss">
.pagination-wrapper {
  border-radius: 62px;
  border: 1px solid rgba(var(--v-primary-base) / 10%);
  box-shadow: 0 0 2px 0 rgba(145 158 171 / 20%),
    0 12px 24px -4px rgba(145 158 171 / 12%), 0 0 12px 0 rgba(137, 80, 252, 0.1);
  padding: 2px 10px;
  background: white;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  transform-origin: center;
  transform: scale(0.9);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover,
  &:focus-within {
    transform: scale(1);
    box-shadow: 0 6px 24px rgba(137, 80, 252, 0.2);
  }

  // Hide auxiliary controls until the user interacts
  .page-info,
  .items-per-page {
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    pointer-events: none;
    transform: translateY(-4px);
    white-space: nowrap;
    transition: opacity 1.5s ease, max-width 1.5s ease, transform 1.5s ease;
  }

  &:hover,
  &:focus-within {
    .page-info,
    .items-per-page {
      opacity: 1;
      max-width: 420px;
      pointer-events: auto;
      transform: translateY(0);
    }
  }
}

.pagination-btn {
  border: none;
  background: #f5f5f5;
  color: #333;
  padding: 4px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.5s ease;
  display: flex;
  align-items: center;
  /* gap: 4px; */
  min-width: 26px;
  height: 26px;
  border-radius: 13px;
  justify-content: center;
}

.pagination-btn:hover:not(.disabled) {
  background: #e0e0e0;
  .next-icon {
    transform: translateX(2px);
  }
  .previous-icon {
    transform: translateX(-2px);
  }
}

[dir="rtl"] .pagination-btn:hover:not(.disabled) {
  .next-icon {
    transform: translateX(-2px);
  }
  .previous-icon {
    transform: translateX(2px);
  }
}

.pagination-btn.active {
  background: var(--v-primary-base, #8950fc);
  color: white;
  font-weight: 600;
}

.pagination-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.pagination-btn.nav-btn {
  background: transparent;
  color: var(--v-primary-base, #8950fc);
  border: 1px solid rgba(var(--v-primary-base) / 20%);
}

.pagination-btn.nav-btn:hover:not(.disabled) {
  background: rgba(var(--v-primary-base) / 10%);
}

.pagination-dots {
  color: #999;
  font-size: 16px;
  padding: 0 8px;
  font-weight: bold;
}

.page-numbers-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.mobile-page-input {
  display: none;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  flex-wrap: nowrap;
}

.mobile-page-input__field {
  flex: 0 0 64px;
  width: 64px;
  border-radius: 999px;
  border: 1px solid rgba(137, 80, 252, 0.35);
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  background: rgba(137, 80, 252, 0.06);
  text-align: center;
}

.mobile-page-input__field:focus {
  outline: none;
  border-color: var(--v-primary-base, #8950fc);
  box-shadow: 0 0 0 2px rgba(137, 80, 252, 0.2);
}

.mobile-page-input__button {
  border: none;
  border-radius: 999px;
  padding: 6px 14px;
  background: var(--v-primary-base, #8950fc);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.mobile-page-input__button:hover {
  opacity: 0.9;
}

.mobile-page-input__summary {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.page-info {
  display: flex;
  align-items: center;
  gap: 8px;
  // Logical equivalent of `margin-left: auto` so the badge hugs the
  // trailing edge of the pagination strip in both LTR and RTL.
  margin-inline-start: auto;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
}

.page-info__label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
  color: #9ca3af;
}

.page-info__badge {
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(124, 58, 237, 0.25);
  transition: transform 0.2s ease;
}

.page-info__badge:active {
  transform: scale(0.96);
}

.page-info__total {
  font-size: 12px;
  color: #6b7280;
}

.items-per-page,
.items-per-page-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 8px;
}

.items-per-page {
  // Logical equivalent of `margin-left: auto` to keep the items-per-page
  // selector pinned to the trailing edge in both LTR and RTL.
  margin-inline-start: auto;
}

.items-per-page__content {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}

.items-per-page-label,
.items-per-page__label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  font-weight: 600;
}

.items-per-page__control {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.items-per-page-select,
.items-per-page__select {
  // Logical padding so the chevron-side gets 32px and the text-side gets
  // 14px regardless of direction.
  padding-block: 6px;
  padding-inline: 14px 32px;
  border-radius: 999px;
  border: 1px solid rgba(137, 80, 252, 0.25);
  background: rgba(137, 80, 252, 0.08);
  color: #4c1d95;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  min-width: auto;
  width: auto;
}

.items-per-page-select:focus,
.items-per-page__select:focus {
  outline: none;
  border-color: var(--v-primary-base, #8950fc);
  box-shadow: 0 0 0 3px rgba(137, 80, 252, 0.15);
}

.items-per-page__chevron {
  position: absolute;
  // Logical end-side inset so the chevron sits next to the select's
  // trailing edge in both LTR (right) and RTL (left).
  inset-inline-end: 14px;
  color: #6741d9;
  pointer-events: none;
}

@media (max-width: 1230px) {
  .pagination-wrapper {
    .page-info,
    .items-per-page {
      display: none;
    }
  }
}

@media (max-width: 634px) {
  .page-numbers-group {
    display: none;
  }

  // Two lines max:
  //   row 1: [prev]  [page input + Go]  [next]
  //   row 2: Page X / Y   Showing a-b of N items
  // .mobile-page-input is flattened so its children order alongside prev/next.
  .mobile-page-input {
    display: contents;
  }

  .pagination-wrapper {
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
  }

  .pagination-wrapper .pagination-prev,
  .pagination-wrapper .pagination-next {
    flex: 1 1 0;
    min-width: 0;
    // Drop side padding so arrows sit the same distance from both edges
    padding-left: 0;
    padding-right: 0;
    gap: 4px;
    white-space: nowrap;
  }

  .pagination-prev {
    order: 1;
    justify-content: flex-start;
  }

  .mobile-page-input__field {
    order: 2;
  }

  .mobile-page-input__button {
    order: 3;
  }

  .pagination-next {
    order: 4;
    justify-content: flex-end;
  }

  // Line break between row 1 and row 2
  .pagination-wrapper::after {
    content: "";
    order: 5;
    flex: 0 0 100%;
    height: 0;
  }

  .mobile-page-input__summary {
    order: 6;
  }

  // Always show item total on mobile (no hover on touch)
  .pagination-wrapper .page-info {
    display: flex;
    order: 7;
    flex: 0 1 auto;
    width: auto;
    margin: 0;
    opacity: 1;
    max-width: none;
    pointer-events: auto;
    transform: none;
  }

  .page-info__label {
    display: none;
  }
}

// Tight phones: icon-only prev/next so row 1 never wraps
@media (max-width: 420px) {
  .pagination-prev span,
  .pagination-next span {
    display: none;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .pagination-wrapper {
    padding: 12px 16px;
    gap: 6px;
  }

  .pagination-btn {
    padding: 6px 12px;
    font-size: 12px;
    min-width: 36px;
    height: 36px;
  }

  .page-info {
    font-size: 12px;
    margin-inline-start: 0;
    width: 100%;
    text-align: center;
    margin-top: 8px;
  }

  .items-per-page-container {
    padding: 0 16px;
  }
}

/* Animation for page transitions */
.pagination-btn.active {
  animation: pageActiveSlide 0.3s ease;
}

@keyframes pageActiveSlide {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
</style>
